import React, { createContext, useContext, useState, useEffect } from 'react';
import { login as apiLogin, ssoExchange } from '../api/client';
import {
  startSsoLogin, ssoLogout, getAuthConfigCached, handleRedirectResult,
} from '../auth/msalClient';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  // null while loading; then { auth_mode, sso_enabled, local_enabled, sso:{...} }
  const [authConfig, setAuthConfig] = useState(null);
  // After redirect-back from Entra ID we may need to push the user to /admin.
  // The AdminPage / AdminLoginPage components watch this and navigate.
  const [postLoginPath, setPostLoginPath] = useState(null);

  const _storeSession = (access_token, role, username) => {
    localStorage.setItem('access_token', access_token);
    localStorage.setItem('user_role', role);
    localStorage.setItem('username', username);
    setUser({ token: access_token, role, username });
  };

  // Bootstrap: restore local session, fetch auth config, complete any pending SSO redirect.
  useEffect(() => {
    (async () => {
      try {
        const token = localStorage.getItem('access_token');
        const role = localStorage.getItem('user_role');
        const username = localStorage.getItem('username');
        if (token && role) {
          setUser({ token, role, username });
        }

        let cfg;
        try {
          cfg = await getAuthConfigCached();
        } catch {
          cfg = { auth_mode: 'local', sso_enabled: false, local_enabled: true };
        }
        setAuthConfig(cfg);

        // If we just came back from Entra ID, finish the exchange.
        if (cfg?.sso_enabled) {
          try {
            const result = await handleRedirectResult();
            if (result?.id_token) {
              const res = await ssoExchange(result.id_token);
              const { access_token, role: r } = res.data;
              const upn = result.account?.username || 'sso-user';
              _storeSession(access_token, r, upn);
              setPostLoginPath(result.returnTo || '/admin');
            }
          } catch (err) {
            console.warn('SSO redirect exchange failed:', err);
          }
        }
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const login = async (username, password) => {
    const res = await apiLogin(username, password);
    const { access_token, role } = res.data;
    _storeSession(access_token, role, username);
    return role;
  };

  /**
   * Kick off SSO sign-in.  This NAVIGATES THE PAGE AWAY — it does not return a
   * role like login() does.  The actual role assignment happens after Entra
   * redirects back and the bootstrap useEffect runs handleRedirectResult().
   */
  const loginSso = async () => {
    await startSsoLogin();
  };

  const logout = () => {
    localStorage.removeItem('access_token');
    localStorage.removeItem('user_role');
    localStorage.removeItem('username');
    setUser(null);
    ssoLogout().catch(() => {});
  };

  const consumePostLoginPath = () => {
    const p = postLoginPath;
    if (p) setPostLoginPath(null);
    return p;
  };

  return (
    <AuthContext.Provider value={{
      user, loading, authConfig,
      login, loginSso, logout,
      consumePostLoginPath,
      ssoEnabled:   !!authConfig?.sso_enabled,
      localEnabled: !!authConfig?.local_enabled,
      isAdmin: user?.role === 'admin',
      isViewer: user?.role === 'viewer',
      canSeeDashboard: user?.role === 'admin' || user?.role === 'viewer',
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be inside AuthProvider');
  return ctx;
}
